import {
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { z } from 'zod';
import { db, updateAdAccountsCloudFunction } from '@/firebase/init';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { ConnectorFromStore, LocalAdAccount, ValidConnector } from '@/types/Connectors';
import { assert } from '@/utils/asserts';
import { NAV_ROUTES } from '@/utils/constants';
import { toastPromise } from '@/utils/toasts';

const connectorNameSchema = z.object({
  name: z.string().min(1, "Connector's name must be at least 2 characters long"),
});

const CONNECTOR_STATUS_MAP = new Map([
  ['ACTIVATION_SUCCESS', 'Activated'],
  ['DEACTIVATION_SUCCESS', 'Deactivated'],
  ['ACTIVATION_AWAITING', 'Activating'],
  ['DEACTIVATION_AWAITING', 'Deactivating'],
  ['READY_FOR_RECONNECTION', 'Reconnecting'],
] as const);

type ConnectorNameSchema = z.infer<typeof connectorNameSchema>;
type ValueType<T> = T extends Map<string, infer V> ? V : never;
type KeyType<T> = T extends Map<infer V, string> ? V : never;

type ConnectorStatusValues = ValueType<typeof CONNECTOR_STATUS_MAP>;
type ConnectorStatusKeys = KeyType<typeof CONNECTOR_STATUS_MAP>;

export function useEditAdAccounts<AdAccount extends LocalAdAccount>() {
  const organizationId = useOrganizationStore((state) => state.selectedOrganization?.id);
  const region = useOrganizationStore((state) => state.selectedOrganization?.region);
  const rawConnector = useLoaderData() as { connector?: ConnectorFromStore<AdAccount> };
  const connector = useMemo(() => {
    return rawConnector?.connector
      ? (rawConnector.connector as ConnectorFromStore<AdAccount>)
      : (rawConnector as ConnectorFromStore<AdAccount>);
  }, [rawConnector]);
  const selectedOrganiation = useOrganizationStore((state) => state.selectedOrganization);
  const [isConnectorReady, setIsConnectorReady] = useState(false);
  const [connectorStatus, setConnectorStatus] =
    useState<ConnectorStatusValues>('Activated');
  const [isConnectorFailure, setIsConnectorFailure] = useState(false);
  assert(connector !== null, 'Connector not found');
  assert(organizationId, 'Organization not found');
  assert(region, 'Region not found');
  const {
    register,
    getValues,
    formState: { errors },
    trigger,
    getFieldState,
    handleSubmit,
  } = useForm<ConnectorNameSchema>({
    resolver: zodResolver(connectorNameSchema),
    defaultValues: { name: connector.name },
    mode: 'onChange',
  });
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const isSetup = status === 'success';
  const navigate = useNavigate();
  const [searchPhrase, setSearchPhrase] = useState<string>('');
  const [data, setData] = useState<AdAccount[]>([]);
  const workspaceId = useWorkspaceIdFromParams() as string;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const revalidator = useRevalidator();
  const displayData = useMemo(
    () =>
      searchPhrase.length === 0
        ? data
        : data.filter((adAccount) =>
            adAccount.name.toLowerCase().includes(searchPhrase.toLowerCase()),
          ),
    [data, searchPhrase],
  );

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, `workspaces/${workspaceId}/connectors/${connector.id}`),
      (doc) => {
        if (doc.exists()) {
          setIsLoading(false);
          const connectorData = doc.data();
          setData(connectorData?.config.accounts);
          setConnectorStatus(
            CONNECTOR_STATUS_MAP.get(
              connectorData?.status as ConnectorStatusKeys,
            ) as ConnectorStatusValues,
          );
          if (connectorData?.status === 'READY_FOR_RECONNECTION') {
            navigate(
              `${NAV_ROUTES.connectorInfo(
                doc.id as ValidConnector,
              )}?workspaceId=${workspaceId}`,
            );
          }
        }
      },
    );

    return unsub;
  }, [connector.id, navigate, workspaceId]);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(
        db,
        `organizations/${selectedOrganiation?.id as string}/organization_connectors`,
        connector.id,
      ),
      (doc) => {
        if (doc.data()?.status === 'CONNECTOR_SUCCESS') {
          setIsConnectorReady(true);
        } else if (doc.data()?.status === 'CONNECTOR_FAILURE') {
          setIsConnectorFailure(true);
        } else {
          setIsConnectorFailure(false);
          setIsConnectorReady(false);
        }
      },
    );
    return unsub;
  }, [connector.id, selectedOrganiation?.id]);

  const onAdAccountClick = useCallback((prev: AdAccount[], adAccountId: string) => {
    const copy = [...prev];
    const index = copy.findIndex((item) => item.id === adAccountId);
    copy[index] = { ...copy[index] };
    copy[index].selected = !copy[index].selected;
    return copy;
  }, []);

  const onUpdateName = useCallback(async () => {
    await trigger('name');
    const { invalid, error } = getFieldState('name');
    if (invalid || error) {
      console.error('Invalid form data');
      return;
    }
    setIsLoading(true);

    const newConnectorName = getValues('name');
    if (newConnectorName === connector.name) {
      setIsLoading(false);
      return;
    }

    try {
      await updateDoc(doc(db, `workspaces/${workspaceId}/connectors`, connector.id), {
        name: newConnectorName,
      });
      revalidator.revalidate();
    } catch (e) {
      console.error(e);
    }
  }, [
    connector.id,
    connector.name,
    getFieldState,
    getValues,
    revalidator,
    trigger,
    workspaceId,
  ]);

  const onUpdate = useCallback(async () => {
    setIsLoading(true);
    const updateRequest = async () =>
      await updateAdAccountsCloudFunction({
        workspaceId,
        accounts: data,
        connectorId: connector.id,
        organizationId,
        region,
      });
    try {
      await toastPromise(
        updateRequest(),
        { success: 'Updated connector data!', error: 'Failed to update connector data' },
        { position: 'bottom-right', hideProgressBar: true, pauseOnHover: false },
      );
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }, [connector.id, data, organizationId, region, workspaceId]);

  return {
    register,
    errors,
    isLoading,
    onUpdate,
    onAdAccountClick,
    displayData,
    searchPhrase,
    setSearchPhrase,
    isSetup,
    connector,
    revalidator,
    navigate,
    setData,
    workspaceId,
    isConnectorReady,
    isConnectorFailure,
    onUpdateName,
    connectorStatus,
    handleSubmit,
    isInitialSetup: connector.isInitialSetup,
  };
}
